import type * as React from 'react'
import { useEffect } from 'react'
import type { JSX } from 'react'

import { useQuery } from '@apollo/client/react'
import { Loader } from '@buffer-mono/popcorn'

import { graphql } from '~publish/graphql'
import {
  ALLOW_LOGGED_OUT_AUTH_REJECTION_CONTEXT_KEY,
  isAuthRejectionError,
} from '~publish/helpers/authRejection'
import { navigateTo } from '~publish/helpers/navigation'
import { getAuthenticatedPathnameFromPublicPath } from '~publish/helpers/publicPublishPaths'
import { runWhenActivated } from '~publish/helpers/runWhenActivated'

import styles from './AuthGate.module.css'

export const GetPublicSession = graphql(/* GraphQL */ `
  query GetPublicSession {
    account {
      id
    }
  }
`)

type AuthGateProps = {
  loggedOut: React.ReactNode
  loading?: React.ReactNode
}

function AuthGateLoading(): JSX.Element {
  return (
    // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- This authentication loader is a live status region.
    <div className={styles.loading} role="status" aria-live="polite">
      <Loader />
      <span>Loading Publish…</span>
    </div>
  )
}

/**
 * Resolves the session for `/p/*` public Publish pages.
 *
 * Mounted only on the public path branch of PublishRoot: logged-out visitors
 * render the public shell, while authenticated visitors leave the public
 * branch entirely via a full-page navigation to the equivalent app route
 * (`/p/schedule` → `/schedule`), where the legacy authenticated flow boots.
 *
 * The GetPublicSession bootstrap opts into logged-out auth rejections, so an
 * expired or missing session resolves here instead of redirecting to login.
 */
export function AuthGate({
  loggedOut,
  loading = <AuthGateLoading />,
}: AuthGateProps): JSX.Element {
  const {
    data,
    loading: isAccountLoading,
    error,
  } = useQuery(GetPublicSession, {
    context: {
      [ALLOW_LOGGED_OUT_AUTH_REJECTION_CONTEXT_KEY]: true,
    },
    errorPolicy: 'all',
    fetchPolicy: 'network-only',
  })

  const account = data?.account
  const hasBootstrapCompleted = !isAccountLoading
  const isBootstrapAuthRejection = isAuthRejectionError(error)
  const shouldRedirectToApp = hasBootstrapCompleted && Boolean(account)

  useEffect(() => {
    if (!shouldRedirectToApp) {
      return
    }

    // Chrome can keep prerendering after a same-origin navigation.
    // Wait so the authenticated app does not start tracking a hidden page.
    return runWhenActivated(document, () => {
      const { pathname, search, hash } = window.location
      const authenticatedPathname =
        getAuthenticatedPathnameFromPublicPath(pathname) ?? '/'

      navigateTo(`${authenticatedPathname}${search}${hash}`)
    })
  }, [shouldRedirectToApp])

  if (!hasBootstrapCompleted || shouldRedirectToApp) {
    return <>{loading}</>
  }

  if (isBootstrapAuthRejection || (!error && account === null)) {
    return <>{loggedOut}</>
  }

  if (error) {
    throw error
  }

  throw new Error('GetPublicSession bootstrap completed without account data')
}
